import lodashGet from "lodash/get";
import dayjs from "dayjs";

import {
  ENDPOINT_CR_COUNTRIES,
  ENDPOINT_CR_RISK_RATINGS,
  ENDPOINT_CR_RISK_CATEGORIES,
  ENDPOINT_CR_UPDATES,
  ENDPOINT_CR_MAP_LAYERS,
  ENDPOINT_RISK_CATEGORIES,
} from "@/common/constants/api";
import {
  RISK_LEVELS,
  RISK_RATING_DESCRIPTIONS,
} from "@/common/constants/controlRisks/risks";
import { LAYER_ID_ACETATE } from "@/common/constants/maps";
import {
  firstDay1MonthAgo,
  daysAgo30Utc,
  monthsAgo1,
} from "@/common/date.utilities";
import { httpGet } from "@/services/api.service";

import { addLayer, removeLayer } from "@/common/maps/layers";
import { addSource, removeSource } from "@/common/maps/sources";
import {
  layerClickEventHandlerOn,
  layerClickEventHandlerOff,
} from "@/common/maps/eventHandler";
import { waitForMapReady } from "@/common/maps/maps";
import lodashHas from "lodash/has";
import lodashCloneDeep from "lodash/cloneDeep";

const SOURCE_ID = "mapbox-terrain";
const LOW_OPACITY = 0.25;
const MID_OPACITY = 0.5;
const HIGH_OPACITY = 0.75;

let layersSourceUrl;
let mapLayers;
const ZONE_ID_FIELD = "Zone_ID";
const COUNTRY_ID_FIELD = "ISO";

export { _getMapLayers as getMapLayers };
export { _getCountries as getCountries };
export { _getRiskTypes as getRiskTypes }; //TODO: Marked for CR Depreciation replaced by _getRiskCategories
export { _getRiskRatings as getRiskRatings };
export { _getCountryDetails as getCountryDetails };
export { _getCountryUpdateArticles as getCountryUpdateArticles };
export { _addToMap as addToMap };
export { _removeFromMap as removeFromMap };
export { _getMapTypeBySelectedRatings as getMapTypeBySelectedRatings };
export { _populateDefaultPreferences as populateDefaultPreferences };
export { _highlightZoneOnMap as highlightZoneOnMap };
export { _getRiskCategoriesConfig as getRiskCategoriesConfig };
export { _getRiskCategories as getRiskCategories };
export { _getRiskCategoriesByGroup as getRiskCategoriesByGroup };
export { _isSubCategorySelected as isSubCategorySelected };
export { _modifyRiskCategoryString as modifyRiskCategoryString };

let riskCategoriesConfig = null;

const _getMapLayers = async () => {
  try {
    if (mapLayers && layersSourceUrl) {
      return true;
    }

    const RESULT = await httpGet({
      url: ENDPOINT_CR_MAP_LAYERS,
      xApiKey: true,
    });
    const { data } = JSON.parse(RESULT);

    if (data) {
      mapLayers = [];
      let sources = [];
      data.forEach((layer) => {
        if (
          lodashGet(layer, "tilesetId") &&
          lodashGet(layer, "type") &&
          lodashGet(layer, "layerName")
        ) {
          sources.push(layer.tilesetId);
          layer.idField =
            layer.type === "country" ? COUNTRY_ID_FIELD : ZONE_ID_FIELD;
          layer.type = layer.type === "country" ? "countries" : "zones";
          layer.name = layer.layerName;
          delete layer.layerName;
          mapLayers.push(layer);
        }
      });
      layersSourceUrl = `mapbox://${sources.join()}`;
    }
    return true;
  } catch (error) {
    Promise.reject(
      "Issue getting map layers in risks.service.js: " + JSON.stringify(error)
    );
    return null;
  }
};

const _getCountries = async (params) => {
  try {
    const RESULT = await httpGet({
      url: ENDPOINT_CR_COUNTRIES,
      xApiKey: true,
    });
    const { data } = JSON.parse(RESULT);
    return data;
  } catch (error) {
    Promise.reject(
      "Issue getting countries in risks.service.js: " + JSON.stringify(error)
    );
    return null;
  }
};

const _getRiskTypes = async (params) => {
  try {
    const RESULT = await httpGet({
      url: ENDPOINT_CR_RISK_CATEGORIES,
      xApiKey: true,
    });
    const { data } = JSON.parse(RESULT);

    return data;
  } catch (error) {
    Promise.reject(
      "Issue getting risk types in risks.service.js: " + JSON.stringify(error)
    );
    return null;
  }
};

const _getRiskRatings = async (params) => {
  try {
    let url = `${ENDPOINT_CR_RISK_RATINGS}?order=asc&offset=0&limit=1000`;

    if (lodashGet(params, "categories")) {
      url += `&riskCategories=${params.categories}`;
    }

    const RESULT = await httpGet({
      url: url,
      xApiKey: true,
    });
    let { data } = JSON.parse(RESULT);
    if (data && params.flattenData) {
      data = _formatData(data);
    }
    return data;
  } catch (error) {
    Promise.reject(
      "Issue getting risks in risks.service.js: " + JSON.stringify(error)
    );
    return null;
  }
};

const _getCountryDetails = async (countryCode) => {
  try {
    const url = `${ENDPOINT_CR_COUNTRIES}/${countryCode.toUpperCase()}/background`;
    const RESULT = await httpGet({
      url: url,
      xApiKey: true,
    });
    const { data } = JSON.parse(RESULT);

    return data;
  } catch (error) {
    Promise.reject(
      "Issue getting risk types in risks.service.js: " + JSON.stringify(error)
    );
    return null;
  }
};

const _getCountryUpdateArticles = async (countryCode, category) => {
  try {
    const categories =
      category && category.id ? `&riskCategories=${category.id}` : "";
    const url = `${ENDPOINT_CR_UPDATES}?order=desc&countries=${countryCode}${categories}&include=riskCategories`;

    const RESULT = await httpGet({
      url: url,
      xApiKey: true,
    });
    const { data } = JSON.parse(RESULT);

    const updates = [];
    if (data && Array.isArray(data)) {
      // AAAS-3738 remove any updates with riskCategory=Travel as per ControlRisks
      data.forEach((update) => {
        let hasTravel = false;
        if (lodashGet(update, "riskCategories")) {
          update.riskCategories.forEach((rc) => {
            if (rc.toUpperCase() === "TRAVEL") {
              hasTravel = true;
            }
          });
        }
        if (!hasTravel) {
          updates.push(update);
        }
      });
    }

    return updates;
  } catch (error) {
    Promise.reject(
      "Issue getting country update articles in risks.service.js: " +
        JSON.stringify(error)
    );
    return null;
  }
};

const _formatData = (data) => {
  let flattenedData = {
    countries: {},
    zones: {},
  };

  if (data && data.length) {
    const lenCountries = data.length;

    RISK_RATING_DESCRIPTIONS.forEach((riskRating) => {
      flattenedData.countries[riskRating.id] = [];
      flattenedData.zones[riskRating.id] = [];
    });

    for (let i = 0; i < lenCountries; i++) {
      const country = data[i];
      if (country) {
        if (country.ratings && country.iso2Code) {
          _getRatingsForCountry(country, flattenedData);
        }

        if (country.riskZones) {
          const lenRiskZones = country.riskZones.length;
          for (let j = 0; j < lenRiskZones; j++) {
            const riskZone = country.riskZones[j];
            riskZone.iso3 = country.iso3Code;
            riskZone.id = 1 + j;
            if (riskZone && riskZone.ratings) {
              _getRatingsForZone(riskZone, flattenedData);
            }
          }
        }
      }
    }
  }
  return flattenedData;
};

const _getRatingsForCountry = (country, data) => {
  return _getRatingsForType({
    country: country,
    data: data,
  });
};

const _getRatingsForZone = (zone, data) => {
  return _getRatingsForType({
    zone: zone,
    data: data,
  });
};

const _getRatingsForType = (options) => {
  const isCountry = !!options.country;
  const ratings = options.country
    ? options.country.ratings
    : options.zone.ratings;
  const lenRatings = ratings.length;
  const lenCategories = RISK_RATING_DESCRIPTIONS.length;
  for (let i = 0; i < lenRatings; i++) {
    const rating = ratings[i];
    const riskCategoryId = lodashGet(rating, "riskCategory.id");
    if (riskCategoryId) {
      for (let j = 0; j < lenCategories; j++) {
        const currentCategoryId = RISK_RATING_DESCRIPTIONS[j].id;
        if (currentCategoryId === riskCategoryId) {
          const ratingLevel = lodashGet(rating, "rating.level");
          if (ratingLevel) {
            if (isCountry) {
              options.data.countries[currentCategoryId].push([
                options.country.iso2Code,
                ratingLevel,
              ]);
            } else {
              options.data.zones[currentCategoryId].push([
                options.zone.code,
                ratingLevel,
              ]);
            }
          }
          break;
        }
      }
    }
  }
};

const getRRListFromRange = (riskScoreRange) => {
  if (!riskScoreRange) {
    riskScoreRange = [1, 2, 3, 4, 5];
  }
  let riskScoresToDisplay = [];
  for (let i = riskScoreRange[0]; i < riskScoreRange[1] + 1; i++) {
    riskScoresToDisplay.push(i);
  }
  return riskScoresToDisplay;
};

const _addToMap = async (params) => {
  await waitForMapReady(params.map);
  _removeFromMap(params);

  let beforeLayer = LAYER_ID_ACETATE;
  if (params.map && params.map.getLayer("City large scale/town small")) {
    beforeLayer = "City large scale/town small";
  }

  let processedData;
  if (params.countries) {
    processedData = _formatData(params.countries);
  } else {
    processedData = await _getRiskRatings({ flattenData: true });
  }

  let desiredRiskType = 1;
  if (params.riskType) {
    desiredRiskType = params.riskType;
  }

  let riskScoresToDisplay = getRRListFromRange(params.riskScoresToDisplay);

  let stuffToHide = _getAoisToHide(
    processedData,
    desiredRiskType,
    riskScoresToDisplay
  );

  addSource(params.map, SOURCE_ID, {
    type: "vector",
    url: layersSourceUrl,
  });

  mapLayers.forEach((layerInfo) => {
    const options = {
      name: layerInfo.name,
      expression: _getStyleExpression(
        layerInfo.idField,
        processedData[layerInfo.type][desiredRiskType]
      ),
      opacityExpression: _getOpacityExpression(
        layerInfo.idField,
        stuffToHide,
        riskScoresToDisplay
      ),
      filterExpression: _getFilterExpression(layerInfo.idField, stuffToHide),
      beforeLayer: beforeLayer,
    };

    if (params.mapClickEvent) {
      options.mapClickEvent = params.mapClickEvent;
    }

    _makeLayer(params.map, options);
  });
};

const _removeFromMap = (params = {}) => {
  if (!params.map) {
    return;
  }

  if (mapLayers && Array.isArray(mapLayers)) {
    mapLayers.forEach((layer) => {
      layerClickEventHandlerOff(params.map, `${layer.name}-fill`);
      removeLayer(params.map, `${layer.name}-fill`);
    });
    removeSource(params.map, SOURCE_ID);
  }
};

function _makeLayer(map, params) {
  const options = {
    id: `${params.name}-fill`,
    type: "fill",
    source: SOURCE_ID,
    "source-layer": params.name,
    layout: {},
    paint: {
      "fill-color": params.expression,
      "fill-opacity": params.opacityExpression
        ? params.opacityExpression
        : MID_OPACITY,
    },
    maxzoom: 8,
    filter: params.filterExpression,
  };

  addLayer(map, options, params.beforeLayer);

  if (params.mapClickEvent) {
    layerClickEventHandlerOn(map, `${params.name}-fill`, params.mapClickEvent);
  }
}

function _getColorByRiskRating(ratingValue) {
  for (let i = 0; i < RISK_LEVELS.length; i++) {
    if (RISK_LEVELS[i].value === ratingValue) {
      return RISK_LEVELS[i].bgColor;
    }
  }
}

function _getStyleExpression(propName, data) {
  let base = ["match", ["get", propName]];

  if (data) {
    for (let i = 0; i < data.length; i++) {
      base.push(data[i][0]);
      base.push(_getColorByRiskRating(data[i][1]));
    }
  }

  base.push("#000000");
  return base;
}

function _getAoisToHide(data, riskType, riskScoresToShow) {
  let countriesToHide = [];
  let zonesToHide = [];
  let countriesToShow = [];
  let zonesToShow = [];

  if (riskType && data && data.countries && data.countries[riskType]) {
    for (let i = 0; i < data.countries[riskType].length; i++) {
      if (riskScoresToShow.indexOf(data.countries[riskType][i][1]) > -1) {
        countriesToShow.push(data.countries[riskType][i][0]);
      } else {
        countriesToHide.push(data.countries[riskType][i][0]);
      }
    }
  }

  if (riskType && data && data.zones && data.zones[riskType]) {
    for (let i = 0; i < data.zones[riskType].length; i++) {
      if (riskScoresToShow.indexOf(data.zones[riskType][i][1]) > -1) {
        zonesToShow.push(data.zones[riskType][i][0]);
      } else {
        zonesToHide.push(data.zones[riskType][i][0]);
      }
    }
  }

  return { countriesToHide, zonesToHide, countriesToShow, zonesToShow };
}

function _getOpacityExpression(idField, stuffToHide, riskScoresToDisplay) {
  if (riskScoresToDisplay.length === RISK_LEVELS.length) {
    return MID_OPACITY;
  }

  let filterConditionsHide = [];
  let filterConditionsShow = [];

  if (idField === COUNTRY_ID_FIELD) {
    for (let i = 0; i < stuffToHide.countriesToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", COUNTRY_ID_FIELD],
        stuffToHide.countriesToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.countriesToShow.length; i++) {
      filterConditionsShow.push([
        "==",
        ["get", COUNTRY_ID_FIELD],
        stuffToHide.countriesToShow[i],
      ]);
    }
  }

  if (idField === ZONE_ID_FIELD) {
    for (let i = 0; i < stuffToHide.zonesToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", ZONE_ID_FIELD],
        stuffToHide.zonesToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.zonesToShow.length; i++) {
      filterConditionsShow.push([
        "==",
        ["get", ZONE_ID_FIELD],
        stuffToHide.zonesToShow[i],
      ]);
    }
  }

  return [
    "case",
    ["any", ...filterConditionsHide],
    LOW_OPACITY,
    ["any", ...filterConditionsShow],
    HIGH_OPACITY,
    MID_OPACITY,
  ];
}

function _getFilterExpression(idField, stuffToHide) {
  let filterConditionsHide = [];

  if (idField === COUNTRY_ID_FIELD) {
    for (let i = 0; i < stuffToHide.countriesToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", COUNTRY_ID_FIELD],
        stuffToHide.countriesToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.countriesToShow.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", COUNTRY_ID_FIELD],
        stuffToHide.countriesToShow[i],
      ]);
    }
  }

  if (idField === ZONE_ID_FIELD) {
    for (let i = 0; i < stuffToHide.zonesToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", ZONE_ID_FIELD],
        stuffToHide.zonesToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.zonesToShow.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", ZONE_ID_FIELD],
        stuffToHide.zonesToShow[i],
      ]);
    }
  }
  return ["any", ...filterConditionsHide];
}

function _getMapTypeBySelectedRatings(selectedRatings) {
  let desiredRiskType = null;
  if (selectedRatings && selectedRatings.id) {
    desiredRiskType = selectedRatings.id;
  }
  return desiredRiskType;
}

function _populateDefaultPreferences(prefs = {}) {
  if (!prefs.controlRisks) {
    prefs.controlRisks = {};
  }
  if (!prefs.controlRisks.riskRatings) {
    prefs.controlRisks.riskRatings = {};
  }

  return prefs;
}

async function _highlightZoneOnMap(params) {
  const LAYER_ID = "zoneHighlight";

  await waitForMapReady(params.map);
  await _getMapLayers();
  addSource(params.map, SOURCE_ID, {
    type: "vector",
    url: layersSourceUrl,
  });

  mapLayers.forEach((layerInfo) => {
    if (layerInfo.idField !== ZONE_ID_FIELD) {
      return;
    }
    try {
      params.map.addLayer({
        id: LAYER_ID,
        type: "line",
        source: SOURCE_ID,
        "source-layer": layerInfo.name,
        layout: {},
        paint: {
          "line-color": "#FFF",
          "line-width": 3,
          "line-opacity": 0,
          "line-opacity-transition": {
            duration: 1000,
            delay: 0,
          },
        },
        filter: ["==", ["get", ZONE_ID_FIELD], params.zoneCode],
      });

      params.map.setPaintProperty(LAYER_ID, "line-opacity", 1);
      setTimeout(() => {
        params.map.setPaintProperty(LAYER_ID, "line-opacity", 0);
      }, 2000);
      setTimeout(() => {
        params.map.removeLayer(LAYER_ID);
      }, 3000);
    } catch (err) {
      console.log(err);
    }
  });
}

const _getRiskCategoriesConfig = async (params, force) => {
  let URL =
    params && params.ui
      ? ENDPOINT_RISK_CATEGORIES + "?ui=true"
      : ENDPOINT_RISK_CATEGORIES;
  if (!riskCategoriesConfig || force) {
    try {
      const RESULT = await httpGet({
        url: URL,
        noCache: true,
      });
      const { data } = JSON.parse(RESULT);
      riskCategoriesConfig = data;

      return data;
    } catch (error) {
      Promise.reject(
        "Issue getting risk categories in risks.service.js: " +
          JSON.stringify(error)
      );
      return null;
    }
  }
};

const _getRiskCategories = (params) => {
  if (params) {
    return _getRiskCategoriesConfig(params, true);
  }
  return lodashCloneDeep(riskCategoriesConfig);
};

const _getRiskCategoriesByGroup = (params) => {
  return riskCategoriesConfig
    .filter((category) => {
      if (params && params.ui) {
        return !category.parentId && !category.ui.hidden;
      }
      return !category.parentId;
    })
    .map((category) => {
      return _appendSubtopics(category);
    });
};

const _appendSubtopics = (_category) => {
  let category = JSON.parse(JSON.stringify(_category));
  if (
    !lodashHas(category, "parentId") &&
    (!category.subtopics || !category.subtopics instanceof Array)
  ) {
    category.subtopics = [];
  }
  for (let i = 0; i < riskCategoriesConfig.length; i++) {
    const cur = riskCategoriesConfig[i];
    if (lodashHas(cur, "parentId") && cur.parentId === category.id) {
      category.subtopics.push(_appendSubtopics(cur));
    }
  }
  return category;
};

const _isRiskCategorySelected = (categoryId, selectionString = "") => {
  let selectedCategories = selectionString ? selectionString.split(",") : [];
  if (selectedCategories.indexOf(categoryId) > -1) {
    return true;
  }
  return selectedCategories.indexOf(categoryId) > -1;
};

const _isSubCategorySelected = (riskCategoryId, selectionString = "") => {
  let selectedRiskCategories = selectionString
    ? selectionString.split(",")
    : [];

  let category = riskCategoriesConfig.forEach((c) => {
    return c.id === riskCategoryId;
  });

  if (
    lodashHas(category, "parentId") &&
    selectedRiskCategories.indexOf(category.id) > -1
  ) {
    return true;
  }
  return _isRiskCategorySelected(riskCategoryId, selectionString);
};

function _modifyRiskCategoryString(riskCategoryId, enabled, currentSelection) {
  let riskCategory = riskCategoriesConfig.filter((category) => {
    return category.id === riskCategoryId;
  });
  let currentSelectionSplit = currentSelection
    ? currentSelection.split(",")
    : [];

  if (!enabled) {
    if (riskCategory[0].parentId) {
      let id = riskCategory[0].id;
      let index = currentSelectionSplit.indexOf(id);
      if (index > -1) {
        currentSelectionSplit.splice(index, 1);
      }
    } else {
      const parent = riskCategory[0];
      let parentIndex = currentSelectionSplit.indexOf(parent.id);
      if (parentIndex > -1) {
        currentSelectionSplit.splice(parentIndex, 1);
      }
      const children = riskCategoriesConfig.filter((c) => {
        return c.parentId === riskCategory[0].id;
      });
      children.forEach((child) => {
        let childIndex = currentSelectionSplit.indexOf(child.id);
        if (childIndex > -1) {
          currentSelectionSplit.splice(childIndex, 1);
        }
      });
    }
  }

  if (enabled && currentSelectionSplit.indexOf(riskCategoryId) < 0) {
    if (riskCategory[0].parentId) {
      currentSelectionSplit.push(riskCategory[0].id);
    } else {
      currentSelectionSplit.push(riskCategory[0].id);
      let children = riskCategoriesConfig.filter((c) => {
        return c.parentId === riskCategory[0].id;
      });
      children.forEach((child) => {
        currentSelectionSplit.push(child.id);
      });
    }
  }
  return currentSelectionSplit.sort().join(",");
}
